import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UserRole } from '@app/core/const/user';
import { TelemetryEventTypes, User } from '@app/core/models';
import { AuthenticationToken } from '@app/core/models/authentication';
import { AUTH_ENDPOINT, authTokenKey, LoginService, LOGOUT_ENDPOINT } from '@app/core/services';
import { SnackBarService } from '@app/shared/services';
import { AppActions, AuthActions } from '@app/store/actions';
import * as fromRoot from '@app/store/reducers';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { jwtDecode } from 'jwt-decode';
import moment from 'moment';
import { map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

@Injectable()
export class AuthEffects {
  readonly actions$ = inject(Actions);
  readonly store = inject<Store<fromRoot.State>>(Store);
  readonly loginService = inject(LoginService);
  readonly snackBarService = inject(SnackBarService);
  readonly router = inject(Router);

  authenticateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.authenticateUser),
      switchMap(({ samlResponse }) => {
        return this.loginService.authenticateUser(samlResponse).pipe(
          map(authData => {
            localStorage.setItem(authTokenKey, authData.jwtToken);
            return AuthActions.loginUser();
          })
        );
      })
    )
  );

  authenticateRedirect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.authenticateRedirect),
        withLatestFrom(this.store.select(fromRoot.getCountry)),
        tap(([_, country]) => {
          window.location.href = `${AUTH_ENDPOINT}?relayState=${country}`;
        })
      ),
    { dispatch: false }
  );

  loginUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginUser),
      map(() => {
        const authToken = this._getTokenWithValidDate();
        if (!authToken) {
          return AuthActions.authenticateRedirect();
        }
        const currentUser = {
          nameId: authToken.nameid,
          email: authToken.email,
          roles: Object.values(UserRole).filter(role => authToken.role.includes(role)),
        } as User;
        return AuthActions.loginUserSuccess({ currentUser });
      })
    )
  );

  loginUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginUserSuccess),
      map(() => AppActions.sendTelemetryEvent({ telemetryData: { eventType: TelemetryEventTypes.userLogin } })),
      tap(() => {
        this.router.navigate(['/']);
      })
    )
  );

  logoutUser$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logoutUser),
        withLatestFrom(this.store.select(fromRoot.getCountry)),
        tap(([_, country]) => {
          const authToken = localStorage.getItem(authTokenKey);
          const thirtySecsFromNow = moment().utc().add(30, 'seconds');
          const cookieExpires = thirtySecsFromNow.format('ddd, DD MMM YYYY HH:mm:ss G\\MT');

          document.cookie = `${authTokenKey}=${authToken};path=/;expires=${cookieExpires}`;
          window.location.href = `${LOGOUT_ENDPOINT}?relayState=${country}`;
        })
      ),
    { dispatch: false }
  );

  logoutUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logoutUserSuccess),
        tap(() => {
          const authToken = localStorage.getItem(authTokenKey);
          if (authToken) {
            localStorage.removeItem(authTokenKey);
          }
          this.router.navigate(['/login']);
        })
      ),
    { dispatch: false }
  );

  forbidden$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.forbidden),
        tap(() => {
          this.snackBarService.openSnackBar('error', [{ key: 'http-errors.generic.forbidden' }], undefined, 5000);
        })
      ),
    { dispatch: false }
  );

  notAuthorized$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.notAuthorized),
        tap(({ titleTranslationKey }) => {
          const messageDuration = 5000;
          if (this._getTokenWithValidDate()) {
            this.snackBarService.openSnackBar(
              'error',
              [{ key: 'http-errors.generic.not-authorized' }],
              titleTranslationKey,
              messageDuration
            );
          } else {
            this.snackBarService.openSnackBar('error', [{ key: 'errors.session-expired' }], undefined, messageDuration);
            setTimeout(() => {
              this.store.dispatch(AuthActions.logoutUser());
            }, messageDuration);
          }
        })
      ),
    { dispatch: false }
  );

  private _getTokenWithValidDate(): AuthenticationToken {
    const authToken = localStorage.getItem(authTokenKey);
    if (!authToken) {
      return;
    }
    const decodedAuthToken: AuthenticationToken = jwtDecode(authToken);
    const expirationDateTime = moment.unix(decodedAuthToken.exp).utc();
    const currentDateTime = moment().utc();
    if (expirationDateTime.isAfter(currentDateTime)) {
      return decodedAuthToken;
    }
    localStorage.removeItem(authTokenKey);
  }
}
